import { useRouter } from '@/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthData, useAuthActions } from '@/store/auth';
import { useSiteStore } from '@/store/site';
import { ApiErrorCode } from '@nf/utils-common/constants';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { deleteUserSession } from '@nf/services/actions/auth/session';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { modalService } from '@/services/modal.service';
import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { depositRelogin } from '@/actions/auth/deposit-relogin';

let isReloginInFlight = false;

export const useAccountError = () => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { userName, siteName, uuid } = useAuthData() ?? {};
	const { signOut } = useAuthActions();
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const locale = useLocale();
	const apiDomain = useSiteStore(state => state.apiDomain);

	const signOutAndRedirect = async (path: string) => {
		if (userName && siteName && uuid) {
			await deleteUserSession({
				name: userName,
				site: siteName,
				uuid
			});
			queryClient.clear();
			signOut();
		}

		router.push(path);
	};

	const alertAndSignOut = (errorCode?: string, errorMsg?: string) => {
		modalService.openJupiterLogoutMessageModal({
			title: errorCode || '',
			message: errorMsg || '',
			eventKey: 'JupiterLogout'
		});
	}

	const silentlyReloginAfterDeposit = async (errorCode?: string, errorMsg?: string, logId?: string) => {
		if (isReloginInFlight) return;
		isReloginInFlight = true;

		if (!apiDomain.ctcdDomain) {
			isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			return;
		}

		const result = await depositRelogin(apiDomain.ctcdDomain, locale);

		if (!result.success) {
			console.error('silentlyReloginAfterDeposit failed:', result.message);
			isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			return;
		}

		window.location.href = result.url;
	};

	useEffect(() => {
		const subscription = modalService.onOpenJupiterLogoutMessageModal
			.subscribe((state) => {
				if (state.props?.eventKey === 'JupiterLogout' && !state.isOpen) {
					router.replace('/auth/logout');
				}
			});

		return () => { 
			subscription.unsubscribe();
		}
	}, [router]);
	
	const handleAccountError = (errorCode?: string, errorMsg?: string, logId?: string, errorDetail?: string) => {
		if (!errorCode) return;
		if (errorCode.startsWith('SP-1067')) {
			const webErrorCode =  errorCode.split('_')[0];
			const singleWalletErrorCode = errorCode.split('_')[1];
			router.push(`/app-error?errorcode=${webErrorCode}&singleWalletErrorCode=${singleWalletErrorCode}&logId=${logId}`);
			return;
		}

		matchEquals(errorCode)
			.on(ApiErrorCode.UM, () => {
				signOutAndRedirect('/um');
			})
			.on(ApiErrorCode.LoginChekckinUM, () => {
				signOutAndRedirect('/um');
			})
			.on(ApiErrorCode.GetBalanceError, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.LoginCheckinKickout, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.Multilogin, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysIdle, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysLogout, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysError, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.InvalidToken, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${ApiErrorCode.InvalidToken}`);
			})
			.on(ApiErrorCode.IpBlock, () => {
				signOutAndRedirect('/limit' + (errorDetail ? `?ErrorDetail=${errorDetail}` : ''));
			})
			.on(ApiErrorCode.LoginChekckinIpBlock302, () => {
				signOutAndRedirect('/limit' + (errorDetail ? `?ErrorDetail=${errorDetail}` : ''));
			})
			.on(ApiErrorCode.NonDepositToDeposit, () => {
				if (isAthena) {
					silentlyReloginAfterDeposit(errorCode, errorMsg, logId);
				} else {
					alertAndSignOut(errorCode, errorMsg);
				}
			})
			.otherwise(() => {
				console.error(`${errorCode}: ${errorMsg}`);
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}`);
			})
	};

	return { handleAccountError };
};